import { useState } from "react";
import {
  Control,
  Controller,
  FieldValues,
  UseFormRegister
} from "react-hook-form";

import { Feature, GeoJsonProperties, Geometry } from "geojson";

import FormField from "@/components/ui/formField";
import RadioButton from "@/components/ui/radio";
import { DatasetSchemaLabel } from "@/types/dataset";
import {
  geoJSONToWKT,
  isGeometryStructure,
  isValidCoorOrderGeoJSON,
  isValidCoorOrderWKT,
  isWKTLineString,
  isWKTPoint,
  isWKTPolygon
} from "@/utils/wkt";

import {
  GEOJSON_INPUT,
  LATITUDE_INPUT,
  LONGITUDE_INPUT,
  WKT_INPUT
} from "./constant";
import { DrawPolygonForm } from "./DrawPolygonForm";
import { GeoJsonInput } from "./GeoJsonInput";

enum EFormatGeom {
  WKT = "wkt",
  COORDINATES = "coordinates",
  GEOJSON = "geojson",
  DRAW_POLYGON = "draw_polygon"
}
export default EFormatGeom;

enum ELabelFormatGeom {
  WKT = "WKT Format",
  COORDINATES = "Coordinates",
  GEOJSON = "GeoJSON",
  DRAW_POLYGON = "Draw Polygon"
}

const listFormatPOI = [
  {
    label: ELabelFormatGeom.WKT,
    name: EFormatGeom.WKT
  },
  {
    label: ELabelFormatGeom.COORDINATES,
    name: EFormatGeom.COORDINATES
  },
  {
    label: ELabelFormatGeom.GEOJSON,
    name: EFormatGeom.GEOJSON
  }
];

const listFormatPolygon = [
  {
    label: ELabelFormatGeom.WKT,
    name: EFormatGeom.WKT
  },
  {
    label: ELabelFormatGeom.GEOJSON,
    name: EFormatGeom.GEOJSON
  },
  {
    label: ELabelFormatGeom.DRAW_POLYGON,
    name: EFormatGeom.DRAW_POLYGON
  }
];

const listFormatLine = [
  {
    label: ELabelFormatGeom.WKT,
    name: EFormatGeom.WKT
  },
  {
    label: ELabelFormatGeom.GEOJSON,
    name: EFormatGeom.GEOJSON
  }
];

interface GeometryFormProps {
  formatGeom: EFormatGeom;
  register: UseFormRegister<FieldValues>;
  name: string;
  control: Control<FieldValues>;
  onDraw: (name: string) => void;
  mapImg?: string;
  drawPolygonState?: Feature<Geometry, GeoJsonProperties>;
  onDeleteDraw: (name: string) => void;
  onClearGeomValue: (value: EFormatGeom, fieldName: string) => void;
  type?: DatasetSchemaLabel; // will updated to enum after all related ticket done
}

export const GeometryForm = ({
  formatGeom,
  register,
  name,
  control,
  onDraw,
  mapImg,
  drawPolygonState,
  onDeleteDraw,
  onClearGeomValue,
  type
}: GeometryFormProps) => {
  const [error, setError] = useState<string | null>(null);

  const getListFormat = (type?: DatasetSchemaLabel) => {
    if (type === "point") {
      return listFormatPOI;
    } else if (type === "polygon") {
      return listFormatPolygon;
    } else if (type === "line") {
      return listFormatLine;
    }
    return [];
  };

  const validateTypeGeom = (value: string) => {
    if (
      (type === "point" && !isWKTPoint(value)) ||
      (type === "polygon" && !isWKTPolygon(value)) ||
      (type === "line" && !isWKTLineString(value))
    ) {
      return false;
    }
    return true;
  };

  return (
    <>
      <div>
        <h1 className="text-txtHeading text-lg font-bold">{name}</h1>
        <p className="text-xs text-txtBody">
          Please select one methods you can paste coordinates, upload structured
          data, or draw directly on the map.
        </p>
      </div>

      <Controller
        name={`format_geom_${name}`}
        control={control}
        rules={{ required: true }}
        render={({ field: { onChange, value } }) => (
          <div className="flex items-center gap-4">
            {getListFormat(type).map((format) => (
              <RadioButton
                key={`${format.name}_${name}`}
                labelClassName="text-xs font-normal text-txtHeading"
                name={`${format.name}_${name}`}
                label={format.label}
                onCheck={() => {
                  onChange(format.name);
                  onClearGeomValue(format.name, name);
                  setError(null);
                }}
                checked={value === format.name}
              />
            ))}
          </div>
        )}
      />
      {error && <div className="text-red-500 text-xs mt-1">{error}</div>}

      {drawPolygonState && formatGeom === "draw_polygon" && (
        <p className="truncate overflow-hidden break-all whitespace-pre-line line-clamp-3 text-xs">
          {geoJSONToWKT(drawPolygonState)?.replace(/^POLYGON\s*/i, "")}
        </p>
      )}

      {formatGeom === "wkt" && (
        <FormField
          name={`${WKT_INPUT}${name}`}
          register={register}
          rules={{
            required: true,
            validate: (value) => {
              if (!validateTypeGeom(value)) {
                setError(
                  "Invalid WKT format. Please check the geometry syntax."
                );
                return false;
              }
              if (!isValidCoorOrderWKT(value)) {
                setError("Latitude and longitude order is incorrect");
                return false;
              }
              setError(null);
              return true;
            }
          }}
          placeHolder="WKT format"
          type="text"
        />
      )}

      {formatGeom === "coordinates" && (
        <div className="flex flex-col gap-2">
          <FormField
            name={`${LATITUDE_INPUT}${name}`}
            register={register}
            rules={{
              required: true,
              validate: (value) => {
                if (Number(value) < -90 || Number(value) > 90) {
                  setError(
                    "Invalid coordinates. Please ensure latitude and longitude values are correct."
                  );
                  return false;
                }
                setError(null);
                return true;
              }
            }}
            placeHolder="Latitude"
            type="text"
          />
          <FormField
            name={`${LONGITUDE_INPUT}${name}`}
            rules={{
              required: true,
              validate: (value) => {
                if (Number(value) < -180 || Number(value) > 180) {
                  setError(
                    "Invalid coordinates. Please ensure latitude and longitude values are correct."
                  );
                  return false;
                }
                setError(null);
                return true;
              }
            }}
            register={register}
            placeHolder="Longitude"
            type="text"
          />
        </div>
      )}

      {formatGeom === "geojson" && (
        <Controller
          name={`${GEOJSON_INPUT}${name}`}
          control={control}
          rules={{
            required: true,
            validate: (value) => {
              try {
                const obj = JSON.parse(value);
                const wkt = geoJSONToWKT(obj) || "";

                if (!validateTypeGeom(wkt) || !geoJSONToWKT(obj)) {
                  setError(
                    "Invalid GeoJSON type. Please check the geometry type."
                  );
                  return false;
                }
                if (!isGeometryStructure(obj)) {
                  setError(
                    "Invalid GeoJSON geometry structure. Please check your GeoJSON data."
                  );
                  return false;
                }
                if (!isValidCoorOrderGeoJSON(obj)) {
                  setError("Latitude and longitude order is incorrect");
                  return false;
                }
                setError(null);
                return true;
              } catch {
                setError(
                  "Invalid GeoJSON geometry structure. Please check your GeoJSON data."
                );
                return false;
              }
            }
          }}
          render={({ field: { onChange, value } }) => (
            <GeoJsonInput onChange={onChange} value={value as string} />
          )}
        />
      )}

      {formatGeom === "draw_polygon" && (
        <DrawPolygonForm
          onDraw={() => onDraw(name)}
          mapImg={mapImg}
          onDelete={() => onDeleteDraw(name)}
        />
      )}
    </>
  );
};
